import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fileToBase64 } from '@/modules/common/utils/file.ts';
import {
  JointlyDocType,
  JointlyDocument,
  JointlyIdentificationDocsFormik,
  documentOptionsJointly,
  validationSchemaJointlyIdentificationDetails,
} from '@/modules/entity/components/individual/jointly/JointlyIdentificationDocs.tsx';
import { useDeleteEntityDocumentMutation } from '@/modules/entity/queries/entity.tsx';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { OnboardingStepProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import {
  useIssuerGetInvestorEditByIdQuery,
  useUpdateInvestorEditMutation,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { Dropzone, SelectButtons, getSelectValueByEnum } from 'ui';
import { useUnsavedChangesStore } from 'ui/hooks';
import { UpdateEntityRequest } from 'api';

const initialValues: JointlyIdentificationDocsFormik = {
  documents: [],
};

export const JointlyIdentificationHolderDetails = ({
  moveForward,
  allowSkip,
}: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data: dataHolder } = useIssuerGetInvestorEditByIdQuery(
    entity_id ? +entity_id : -1,
    !entity_id || +entity_id === 0 || isNaN(+entity_id),
  );
  const [docType, setDocType] = useState<JointlyDocType>(JointlyDocType.JointlyPassport);
  const [isFileLoading, setIsFileLoading] = useState(false);

  const currentDocuments = useMemo(() => {
    const documents = (dataHolder?.data?.new_data?.entity_data?.document_files || []).filter(
      item =>
        item.type === JointlyDocType.JointlyPassport ||
        item.type?.includes(JointlyDocType.JointlyDriverLicense),
    );

    if (documents?.[0]?.type) {
      if (documents?.[0]?.type?.includes(JointlyDocType.JointlyDriverLicense)) {
        setDocType(JointlyDocType.JointlyDriverLicense);
      } else {
        setDocType(documents?.[0]?.type as JointlyDocType);
      }
    }

    return documents;
  }, [dataHolder?.data?.new_data?.entity_data?.document_files]);

  const formik = useFormik<JointlyIdentificationDocsFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: validationSchemaJointlyIdentificationDetails,
    initialValues,
    onSubmit: async values => {
      setDirty(false);
      const currentDocuments = (
        dataHolder?.data?.new_data?.entity_data?.document_files || []
      ).filter(
        item =>
          item.type === JointlyDocType.JointlyPassport ||
          item.type === JointlyDocType.JointlyDriverLicense,
      );
      if (currentDocuments.length < 1 && values.documents.length < 1) {
        formik.setFieldError('documents', 'Please upload document');
        return;
      }
      const documents =
        values?.documents?.filter(item => !item.data?.toLowerCase()?.startsWith('http')) ?? [];
      if (documents?.length > 0) {
        await updateEntity({
          id: dataHolder?.data?.id,
          user_data: dataHolder?.data?.new_data?.user_data,
          dividend_reinvestment: dataHolder?.data?.new_data?.dividend_reinvestment,
          entity_data: {
            ...dataHolder?.data?.new_data?.entity_data,
            documents,
          } as UpdateEntityRequest,
        });
      }
      moveForward && moveForward();
    },
  });

  const [dirty, setDirty] = useState(formik.dirty);
  const setUnsavedChanges = useUnsavedChangesStore(state => state.setUnsavedChanges);
  setUnsavedChanges(dirty);
  useEffect(() => {
    setDirty(formik.dirty);
  }, [formik.dirty]);

  const { mutateAsync: updateEntity, isLoading } = useUpdateInvestorEditMutation({
    setErrors: formik.setErrors,
  });

  const { mutateAsync: deleteDocument, isLoading: isDocumentDeletion } =
    useDeleteEntityDocumentMutation({});

  const handleChangeType = (docType: JointlyDocType) => {
    setDocType(docType || JointlyDocType.JointlyPassport);
  };

  const onFileAccepted = async (file: File) => {
    setIsFileLoading(true);

    const base64 = await fileToBase64(file, true);

    await formik.setFieldValue('documents', [
      ...formik.values.documents,
      {
        type: docType,
        data: base64,
      },
    ]);
    formik.setFieldError('documents', '');

    setIsFileLoading(false);
  };

  const onFileClear = useCallback(async () => {
    // const current = formik.values.documents.find(doc => doc.type === docType);
    // if (current?.data?.includes('http')) {
    //   await deleteDocument({ entity_id: +entity_id || 0, type: docType });
    // }
    await formik.setFieldValue(
      'documents',
      formik.values.documents.filter(doc => doc.type !== docType),
    );
    formik.setFieldError('documents', '');
  }, [deleteDocument, docType, entity_id, formik]);

  useEffect(() => {
    formik.setFieldValue(
      'documents',
      currentDocuments.map<JointlyDocument>(item => ({
        data: item.url as string,
        type: item.type as string,
      })),
    );
  }, [currentDocuments]);

  return (
    <OnboardingHolderWrapper
      title="Jointly identification documents"
      onClickContinue={formik.handleSubmit}
      onClickSkip={
        allowSkip
          ? () => {
              setDirty(false);
              setTimeout(() => {
                moveForward && moveForward();
              });
            }
          : undefined
      }
      formik={formik}
      isLoading={isLoading}
    >
      <>
        <SelectButtons
          options={documentOptionsJointly}
          selected={getSelectValueByEnum<JointlyDocType>(docType)}
          onChange={handleChangeType}
        />
        <div className="w-full flex flex-col items-start justify-center space-y-4">
          <div className="w-full flex flex-col space-y-1">
            <p className="text-gray-900 text-[14px] leading-[22px]">Upload image</p>
            <p className="text-gray-600 text-[12px] leading-[20px]">JPG, PNG or PDF. Up to 10 mb</p>
          </div>
          <Dropzone
            onFileAccepted={onFileAccepted}
            loading={isFileLoading || isDocumentDeletion}
            value={formik.values.documents?.find(item => item.type === docType)?.data}
            onFileClear={onFileClear}
            error={formik.errors.documents as string}
          />
        </div>
      </>
    </OnboardingHolderWrapper>
  );
};
